import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logger } from "../../utils/logger";
import { toast } from "react-toastify";
import { actionErrorMessage } from "../../utils/network";
import { useRole } from "../../hooks/useRole";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { DoctorAPI, DoctorListItem, DoctorFull } from "../../services/doctor.service";
import { AddDoctorModal } from "../../components/doctors/AddDoctorModal";
import { EditDoctorModal } from "../../components/doctors/EditDoctorModal";
import { BlockDoctorTimeModal } from "../../components/doctors/BlockDoctorTimeModal";
import { PhoneIcon, MailIcon } from "../../components/icons";
import { OnboardingAPI } from "../../services/onboarding.service";
// ── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name: string) =>
  name.replace(/^Dr\.\s*/i,"").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();

const formatSchedule = (schedules: DoctorListItem["availability_schedules"]) => {
  if (!schedules.length) return "Not set";
  const DAY_ABBR = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const uniqueDays = [...new Set(schedules.map(s => s.day_of_week))].sort((a,b) => a-b);
  return uniqueDays.map(d => DAY_ABBR[d]).filter(Boolean).join(", ");
};

const STATUS_STYLE: Record<string,{pill:string;dot:string}> = {
  Present:   { pill:"bg-green-50 text-green-600 border border-green-200",  dot:"bg-green-500" },
  Break:     { pill:"bg-yellow-50 text-yellow-600 border border-yellow-200", dot:"bg-yellow-500" },
  "On Leave":{ pill:"bg-red-50 text-red-500 border border-red-200",        dot:"bg-red-500" },
  Absent:          { pill:"bg-gray-100 text-gray-500 border border-gray-200",    dot:"bg-gray-400" },
  "Not Available": { pill:"bg-slate-100 text-slate-500 border border-slate-200", dot:"bg-slate-400" },
};

// ── Inline SVG Icons matching Figma exactly ──────────────────────────────────
const GradCapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const PinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const UsersSmIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const CalSmIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/>
    <line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>
  </svg>
);
const EditSmIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IdCardIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
  </svg>
);
const BriefcaseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

// ── ⋯ Menu ────────────────────────────────────────────────────────────────────
const CardMenu: React.FC<{ doctor: DoctorListItem; onEdit:()=>void; onBlockTime:()=>void; onDeactivate:()=>void; onDelete:()=>void }> = ({ doctor, onEdit, onBlockTime, onDeactivate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o=>!o)} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center text-xl leading-none font-bold">⋯</button>
      {open && (
        <div className="absolute right-0 top-7 bg-white border border-gray-100 rounded-xl shadow-xl z-20 w-44 py-1 overflow-hidden">
          <button onClick={()=>{setOpen(false);onEdit();}} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <EditSmIcon/> Edit Doctor
          </button>
          <button onClick={()=>{setOpen(false);onBlockTime();}} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Block Time
          </button>
          <button onClick={()=>{setOpen(false);onDeactivate();}} className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${doctor.is_active ? "text-red-500 hover:bg-gray-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
            {doctor.is_active
              ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            }
            {doctor.is_active ? "Deactivate" : "Reactivate"}
          </button>
          <button onClick={()=>{setOpen(false);onDelete();}} className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 flex items-center gap-2 text-red-600 border-t border-gray-100">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
            Delete Permanently
          </button>
        </div>
      )}
    </div>
  );
};

// ── Profile Modal/Overlay ─────────────────────────────────────────────────────
const ProfileModal: React.FC<{ doctor: DoctorFull; onClose:()=>void; onEdit:()=>void; isAdmin: boolean }> = ({ doctor, onClose, onEdit, isAdmin }) => {
  const navigate = useNavigate();
  const st = STATUS_STYLE[doctor.status] || STATUS_STYLE.Absent;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full min-w-0 max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 p-6 pb-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #38bdf8, #2563eb)" }}>
              {getInitials(doctor.name)}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-gray-900 truncate">{doctor.name}</h2>
              <p className="text-sm text-gray-500 mt-0.5 truncate">{doctor.specialization}</p>
              <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full mt-1.5 font-medium ${st.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>
                {doctor.status}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={()=>{onClose();navigate(`/roster?doctorId=${doctor.display_id || doctor.doctor_id}`);}} className="flex items-center gap-1.5 px-3 min-h-[44px] text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
              <CalSmIcon/> Roster
            </button>
            {isAdmin && (
  <button onClick={onEdit} className="flex items-center gap-1.5 px-3 min-h-[44px] text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600">
    <EditSmIcon/> Edit
  </button>
)}
            <button onClick={onClose} className="ml-1 text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 px-6 pb-4">
          {[
            { label:"Rating", value:"—" },
            { label:"Patients", value: doctor.total_patients != null ? doctor.total_patients.toLocaleString() : "—" },
            { label:"Fee", value: doctor.consultation_fee ? `₹${doctor.consultation_fee}` : "—" },
          ].map((s,i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Today's Patients */}
        <div className="mx-6 mb-4 bg-blue-50 rounded-xl px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-600 font-medium flex items-center gap-1.5"><CalSmIcon/>Today's Patients</p>
            <p className="text-xs text-gray-400 mt-0.5">{doctor.today_upcoming ?? 0} upcoming • {doctor.today_cancelled ?? 0} cancelled</p>
          </div>
          <span className="text-2xl font-bold text-blue-600">{doctor.today_upcoming ?? 0}</span>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-6 pb-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Professional Details</h3>
            <div className="space-y-3">
              {doctor.qualifications && (
                <div className="flex gap-2.5">
                  <span className="text-blue-400 mt-0.5 flex-shrink-0"><GradCapIcon/></span>
                  <div><p className="text-xs text-gray-400">Qualifications</p><p className="text-sm text-gray-800">{doctor.qualifications}</p></div>
                </div>
              )}
              <div className="flex gap-2.5">
                <span className="text-blue-400 mt-0.5 flex-shrink-0"><ClockIcon/></span>
                <div>
                  <p className="text-xs text-gray-400">Experience & Schedule</p>
                  <p className="text-sm text-gray-800">
                    {doctor.experience_years ? `${doctor.experience_years} years exp` : "—"}
                    {doctor.experience_years ? " • " : ""}
                    {formatSchedule(doctor.availability_schedules)}
                  </p>
                </div>
              </div>
              {doctor.room_location && (
                <div className="flex gap-2.5">
                  <span className="text-blue-400 mt-0.5 flex-shrink-0"><PinIcon/></span>
                  <div><p className="text-xs text-gray-400">Location</p><p className="text-sm text-gray-800">{doctor.room_location}</p></div>
                </div>
              )}
              <div className="flex gap-2.5">
                <span className="text-blue-400 mt-0.5 flex-shrink-0"><BriefcaseIcon/></span>
                <div><p className="text-xs text-gray-400">Department</p><p className="text-sm text-gray-800">{doctor.department}</p></div>
              </div>
              {doctor.registration_number && (
                <div className="flex gap-2.5">
                  <span className="text-blue-400 mt-0.5 flex-shrink-0"><IdCardIcon/></span>
                  <div><p className="text-xs text-gray-400">MCI Registration</p><p className="text-sm text-gray-800">{doctor.registration_number}</p></div>
                </div>
              )}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Contact Information</h3>
            <div className="space-y-3">
              {doctor.phone && (
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400"><PhoneIcon/></span>
                  <p className="text-sm text-gray-800">{doctor.phone}</p>
                </div>
              )}
              {doctor.email && (
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400"><MailIcon/></span>
                  <p className="text-sm text-gray-800">{doctor.email}</p>
                </div>
              )}
              {!doctor.phone && !doctor.email && <p className="text-sm text-gray-400">No contact info added</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export const DoctorListPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
  const [deptFilter, setDeptFilter] = useState("All Departments");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [departments, setDepartments] = useState<string[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<DoctorFull | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [profileDoctor, setProfileDoctor] = useState<DoctorFull | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [blockTimeDoctor, setBlockTimeDoctor] = useState<DoctorListItem | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const { canOr } = useRole();
  const navigate = useNavigate();

useEffect(() => {
  OnboardingAPI.getMe()
    .then(me => {
      logger.info("DoctorListPage role loaded", { role: me.role });
      setCurrentRole(me.role ?? "");
    })
    .catch(err => {
      logger.error("[DoctorListPage] getMe failed", err instanceof Error ? err : undefined);
      // getMe failed — default to showing admin controls
      // Backend role guard still protects actual API calls
      setCurrentRole("Admin");
    });
}, []);

// null = still loading → show controls; string = resolved
const roleIsAdmin = currentRole === null || currentRole === "Admin";
// Reaching this page at all is doctors.view (App.tsx). Add / Edit / Block Time /
// Deactivate / Delete are all doctors.manage on the server — including PUT
// /doctors/{id} and POST /block-time, which used to check users.role instead —
// so the affordances follow that grant, with the role as the legacy fallback.
// A View Doctors-only role gets the list, profiles and roster, read-only.
const isAdmin = canOr("doctors.manage", roleIsAdmin);
  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { is_active: showInactive ? undefined : true };
      if (search) params.search = search;
      if (deptFilter !== "All Departments") params.department = deptFilter;
      const data = await DoctorAPI.list(params);
      const filtered = statusFilter === "All Status" ? data : data.filter(d => d.status === statusFilter);
      setDoctors(filtered);
    } catch { toast.error("Failed to load doctors"); }
    finally { setLoading(false); }
  }, [search, deptFilter, statusFilter, showInactive]);

  useEffect(() => { DoctorAPI.getDepartments().then(setDepartments).catch(()=>{}); }, []);
  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const openProfile = async (doctor: DoctorListItem) => {
    setProfileLoading(true);
    try {
      const full = await DoctorAPI.get(doctor.doctor_id);
      setProfileDoctor(full);
    } catch { toast.error("Failed to load profile"); }
    finally { setProfileLoading(false); }
  };
  const openEdit = async (doctor: DoctorListItem) => {
    setEditLoading(true);
    try {
      const full = await DoctorAPI.get(doctor.doctor_id);
      setEditingDoctor(full);
    } catch { toast.error("Failed to load doctor details"); }
    finally { setEditLoading(false); }
  };

  const handleDeactivate = async (doctor: DoctorListItem) => {
    const action = doctor.is_active ? "deactivate" : "reactivate";
    if (!window.confirm(`Are you sure you want to ${action} ${doctor.name}?`)) return;
    try {
      await DoctorAPI.update(doctor.doctor_id, { is_active: !doctor.is_active });
      toast.success(`Doctor ${action}d`);
      fetchDoctors();
    } catch (err) { toast.error(actionErrorMessage(err, `Failed to ${action} doctor`)); }
  };

  const handleDelete = async (doctor: DoctorListItem) => {
    if (!window.confirm(`Permanently delete ${doctor.name}? This cannot be undone.`)) return;
    try {
      await DoctorAPI.delete(doctor.doctor_id);
      toast.success("Doctor deleted");
      fetchDoctors();
    } catch (err) { toast.error(actionErrorMessage(err, "Failed to delete doctor")); }
  };
  const exportToCSV = () => {
  if (doctors.length === 0) {
    toast.info("No doctors to export");
    return;
  }

  const formatSchedule = (schedules: DoctorListItem["availability_schedules"]) => {
    if (!schedules.length) return "Not set";
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const active = schedules.map(s => days[s.day_of_week]).join("-");
    const first = schedules[0];
    const fmt = (t: string) => {
      const [h, m] = t.split(":");
      const hr = parseInt(h);
      return `${hr > 12 ? hr - 12 : hr || 12}${m !== "00" ? `:${m}` : ""} ${hr >= 12 ? "PM" : "AM"}`;
    };
    return `${active}, ${fmt(first.start_time)}-${fmt(first.end_time)}`;
  };

  const headers = [
    "Name",
    "Specialization",
    "Department",
    "Registration No.",
    "Qualifications",
    "Experience (yrs)",
    "Consultation Fee (INR)",
    "Room/Location",
    "Status",
    "Schedule",
    "Active",
  ];

  const rows = doctors.map(d => [
    d.name,
    d.specialization,
    d.department,
    d.registration_number ?? "",
    d.qualifications ?? "",
    d.experience_years ?? "",
    d.consultation_fee ?? "",
    d.room_location ?? "",
    d.status,
    formatSchedule(d.availability_schedules),
    d.is_active ? "Yes" : "No",
  ]);

  const escape = (val: any) => {
    const str = String(val);
    // wrap in quotes if it contains comma, newline, or double-quote
    return str.includes(",") || str.includes("\n") || str.includes('"')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const csv = [headers, ...rows]
    .map(row => row.map(escape).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const date = new Date().toISOString().slice(0, 10); // "2026-04-11"
  link.download = `doctors-${date}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${doctors.length} doctor${doctors.length > 1 ? "s" : ""}`);
};
  const stats = {
    total: doctors.length,
    present: doctors.filter(d => d.status === "Present" && d.is_active !== false).length,
    departments: new Set(doctors.map(d => d.department)).size,
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Doctors" searchMode="doctors" />
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Doctor Management</h1>
              <p className="text-sm text-gray-400 mt-0.5">View and manage all doctors and their schedules</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
  onClick={exportToCSV}
  className="flex items-center gap-2 px-4 min-h-[44px] text-sm border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 font-medium"
>
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>
  </svg>
  Export
</button>
              <button
                onClick={() => setShowInactive(v=>!v)}
                className={`flex items-center gap-2 px-4 min-h-[44px] text-sm border rounded-lg font-medium transition-colors ${
                  showInactive ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                {showInactive ? "Showing All" : "Show Inactive"}
              </button>
              {isAdmin && (
  <button
    onClick={() => setIsAddOpen(true)}
    className="flex items-center gap-2 px-4 min-h-[44px] text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
  >
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14"/><path d="M12 5v14"/>
    </svg>
    Add Doctor
  </button>
)}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { gradient:"from-blue-400 to-blue-600", label:"Total Doctors", value:stats.total, badge:"Total", badgeColor:"text-blue-600 bg-blue-50",
                icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/><path d="M8 15v1a6 6 0 0 0 6 6a6 6 0 0 0 6-6v-4"/><circle cx="20" cy="10" r="2"/></svg> },
              { gradient:"from-green-400 to-green-600", label:"Present Today", value:stats.present, badge:"Active", badgeColor:"text-green-600 bg-green-50",
                icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> },
              { gradient:"from-purple-400 to-purple-600", label:"Departments", value:stats.departments, badge:"Active", badgeColor:"text-purple-600 bg-purple-50",
                icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg> },
              { gradient:"from-orange-400 to-orange-500", label:"Patients Scheduled Today", value:0, badge:"Today", badgeColor:"text-orange-500 bg-orange-50",
                icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
            ].map((s,i) => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-11 h-11 bg-gradient-to-br ${s.gradient} rounded-xl flex items-center justify-center`}>{s.icon}</div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.badgeColor}`}>{s.badge}</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Search + Filters */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                </span>
                <input
                  className="w-full pl-9 pr-4 min-h-[44px] text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Search by name, specialization, or department..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="px-3 min-h-[44px] text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 text-gray-600 bg-white">
                <option>All Departments</option>
                {departments.map(d => <option key={d}>{d}</option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 min-h-[44px] text-base sm:text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 text-gray-600 bg-white">
                <option>All Status</option>
                <option>Present</option>
                <option>Break</option>
                <option>On Leave</option>
                <option>Absent</option>
              </select>
            </div>
          </div>

          {/* Doctor Cards */}
          {profileLoading && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-2xl p-6 text-gray-500 text-sm">Loading profile...</div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400 text-sm">Loading doctors...</div>
          ) : doctors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2 text-gray-300"><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/><path d="M8 15v1a6 6 0 0 0 6 6a6 6 0 0 0 6-6v-4"/><circle cx="20" cy="10" r="2"/></svg>
              <p className="text-sm">No doctors found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {doctors.map(doctor => {
                const st = STATUS_STYLE[doctor.status] || STATUS_STYLE.Absent;
                return (
                  <div key={doctor.doctor_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative">
                    {/* DotMenu lives outside the faded layer so it's always full-opacity */}
                    {isAdmin && (
                      <div className="absolute top-5 right-5 z-10">
                        <CardMenu doctor={doctor} onEdit={() => openEdit(doctor)} onBlockTime={() => setBlockTimeDoctor(doctor)} onDeactivate={() => handleDeactivate(doctor)} onDelete={() => handleDelete(doctor)} />
                      </div>
                    )}
                    <div className="p-5">
                    {/* Card Header */}
                    <div className="flex items-start mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0 pr-6">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #38bdf8, #2563eb)" }}>
                          {getInitials(doctor.name)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm leading-tight">{doctor.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{doctor.specialization}</p>
                          {doctor.is_active === false ? (
                            <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full mt-1 font-medium bg-red-50 text-red-600 border border-red-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"/>
                              Inactive
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full mt-1 font-medium ${st.pill}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>
                              {doctor.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dept badge */}
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md font-medium mb-3 bg-cyan-50 text-cyan-600 border border-cyan-200">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                      {doctor.department}
                    </span>

                    {/* Today's Patients */}
                    <div className="bg-blue-50 rounded-xl px-3 py-2 mb-3 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-500 font-medium flex items-center gap-1"><UsersSmIcon/>Today's Patients</p>
                        <p className="text-xs text-gray-400 mt-0.5">{doctor.today_upcoming ?? 0} upcoming • {doctor.today_cancelled ?? 0} cancelled</p>
                      </div>
                      <span className="text-lg font-bold text-blue-500">{doctor.today_upcoming ?? 0}</span>
                    </div>

                    {/* Info rows */}
                    <div className="space-y-1.5 mb-3">
                      {doctor.qualifications && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5"><span className="text-gray-400 flex-shrink-0"><GradCapIcon/></span>{doctor.qualifications}</p>
                      )}
                      <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <span className="text-gray-400 flex-shrink-0"><ClockIcon/></span>
                        {doctor.experience_years ? `${doctor.experience_years} yrs exp` : ""}
                        {doctor.experience_years ? " • " : ""}
                        {formatSchedule(doctor.availability_schedules)}
                      </p>
                      {doctor.room_location && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5"><span className="text-gray-400 flex-shrink-0"><PinIcon/></span>{doctor.room_location}</p>
                      )}
                    </div>

                    {/* Stats row */}
                    <div className="flex gap-4 text-center border-t border-gray-100 pt-3 mb-3">
                      <div className="flex-1"><p className="text-base font-bold text-gray-800">—</p><p className="text-xs text-gray-400">Rating</p></div>
                      <div className="flex-1"><p className="text-base font-bold text-gray-800">{doctor.total_patients != null ? doctor.total_patients.toLocaleString() : '—'}</p><p className="text-xs text-gray-400">Total Patients</p></div>
                      <div className="flex-1"><p className="text-base font-bold text-gray-800">{doctor.consultation_fee ? `₹${doctor.consultation_fee}` : "—"}</p><p className="text-xs text-gray-400">Fee</p></div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/roster?doctorId=${doctor.display_id || doctor.doctor_id}`)}
                        className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
                      >
                        <CalSmIcon/> Schedule
                      </button>
                      <button onClick={() => openProfile(doctor)} className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        Profile
                      </button>
                    </div>
                    </div>{/* end faded content wrapper */}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddDoctorModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onSuccess={fetchDoctors} />

      {editLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl p-6 text-gray-500 text-sm">Loading doctor...</div>
        </div>
      )}
      {editingDoctor && (
        <EditDoctorModal
          isOpen={!!editingDoctor}
          doctor={editingDoctor}
          onClose={() => setEditingDoctor(null)}
          onSuccess={() => { setEditingDoctor(null); fetchDoctors(); window.dispatchEvent(new Event("doctorUpdated")); }}
        />
      )}

      {profileDoctor && (
        <ProfileModal
  doctor={profileDoctor}
  onClose={() => setProfileDoctor(null)}
  onEdit={() => { setEditingDoctor(profileDoctor); setProfileDoctor(null); }}
  isAdmin={isAdmin}
/>
      )}

      {blockTimeDoctor && (
        <BlockDoctorTimeModal
          isOpen={!!blockTimeDoctor}
          doctorId={blockTimeDoctor.doctor_id}
          doctorName={blockTimeDoctor.name}
          onClose={() => setBlockTimeDoctor(null)}
          onSuccess={() => fetchDoctors()}
        />
      )}
    </div>
  );
};

export default DoctorListPage;